import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, serverTimestamp, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useApplicantContext } from '../../contexts/ApplicantContext';
import { Card, Button, Input, Textarea, Select, Label, Badge, HelperText } from './../ui';
import { Plus, Pencil, Trash2, Check, X, ChevronDown, ChevronUp, ExternalLink, Info } from 'lucide-react';
import { humanize } from '../../lib/humanize';
import { format, parseISO } from 'date-fns';
import { SchoolSearch, type SchoolSearchResult } from '../SchoolSearch';
import { getDiscoverySnapshotById } from '../../lib/addDiscoverySchoolAsTarget';
import type { TargetSchool, ScorecardSnapshot } from '@scholarship-app/types';
import { US_STATES } from '../../lib/usStates';
import { Monogram } from '../Monogram';

interface Props {
  editing: boolean;
}

function genId(prefix: string) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

const empty = (): Omit<TargetSchool, 'school_id' | 'created_at' | 'updated_at'> => ({
  school_name: '',
  school_short_name: '',
  tier: 'backup',
  rank: 1,
  school_type: 'private',
  city: '',
  state: '',
  admission_deadlines: {
    early_action: null,
    early_decision: null,
    regular_decision: null,
    rolling: false,
  },
  applied: false,
  admitted: false,
  decision_date: null,
  notes: '',
  institutional_aid_link: null,
  honors_program_link: null,
  archived: false,
  scorecard_id: null,
  scorecard_data: null,
  scorecard_fetched_at: null,
});

export function ProfileTargetSchoolsEditor({ editing }: Props) {
  const { membership } = useAuth();
  const { selectedApplicantId } = useApplicantContext();
  const [schools, setSchools] = useState<TargetSchool[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<Partial<TargetSchool> | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);

  useEffect(() => {
    if (!db || !membership || !selectedApplicantId) return;
    const ref = collection(db, 'households', membership.householdId, 'applicants', selectedApplicantId, 'target_schools');
    const unsub = onSnapshot(ref, (snap) => {
      const list = snap.docs.map((d) => d.data() as TargetSchool).filter((s) => !s.archived);
      list.sort((a, b) => {
        if (a.tier !== b.tier) return a.tier === 'primary' ? -1 : 1;
        return a.rank - b.rank;
      });
      setSchools(list);
    });
    return () => unsub();
  }, [membership, selectedApplicantId]);

  function startAdd() {
    setAdding(true);
    setExpandedId(null);
    setDraft({ ...empty() });
    setDraftId(null);
  }
  function startEdit(school: TargetSchool) {
    setAdding(false);
    setExpandedId(school.school_id);
    setDraft({ ...school });
    setDraftId(school.school_id);
  }
  function cancel() {
    setAdding(false);
    setDraft(null);
    setDraftId(null);
  }
  async function save() {
    if (!db || !membership || !selectedApplicantId || !draft) return;
    try {
      const id = draftId ?? genId('school');
      const ref = doc(db, 'households', membership.householdId, 'applicants', selectedApplicantId, 'target_schools', id);
      const isNew = !draftId;
      await setDoc(ref, {
        ...draft,
        school_id: id,
        updated_at: serverTimestamp(),
        ...(isNew ? { created_at: serverTimestamp(), archived: false } : {}),
      }, { merge: true });
      cancel();
    } catch (e) {
      alert('Save failed: ' + (e instanceof Error ? e.message : String(e)));
    }
  }
  async function remove(school: TargetSchool) {
    if (!db || !membership || !selectedApplicantId) return;
    if (!confirm(`Remove target school: "${school.school_name}"?`)) return;
    try {
      const ref = doc(db, 'households', membership.householdId, 'applicants', selectedApplicantId, 'target_schools', school.school_id);
      await deleteDoc(ref);
    } catch (e) {
      alert('Delete failed: ' + (e instanceof Error ? e.message : String(e)));
    }
  }
  function toggleExpand(id: string) {
    if (expandedId === id && !draftId) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      setDraft(null);
      setDraftId(null);
    }
  }

  return (
    <div className="space-y-2">
      {schools.length === 0 && !adding && (
        <p className="text-sm text-soft">No target schools yet.</p>
      )}
      {schools.map((s) => {
        const isExpanded = expandedId === s.school_id;
        const isEditingThis = draftId === s.school_id;
        return (
          <Card key={s.school_id} className="overflow-hidden">
            <button
              type="button"
              className="w-full px-3 py-2 flex items-center justify-between gap-2 hover:bg-cream text-left"
              onClick={() => toggleExpand(s.school_id)}
            >
              <div className="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
                <Monogram name={s.school_short_name || s.school_name} size={28} />
                <span className="text-sm font-medium text-ink">{s.school_name}</span>
                <Badge variant={s.tier === 'primary' ? 'indigo' : 'gray'}>{humanize(s.tier)}</Badge>
                <span className="text-xs text-soft">Rank {s.rank}</span>
                {s.applied && <Badge variant="blue">Applied</Badge>}
                {s.admitted && <Badge variant="green">Admitted</Badge>}
              </div>
              {isExpanded ? <ChevronUp size={16} className="text-soft" /> : <ChevronDown size={16} className="text-soft" />}
            </button>
            {isExpanded && (
              <div className="px-3 py-3 border-t border-line">
                {isEditingThis && draft ? (
                  <SchoolEditForm draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} />
                ) : (
                  <div className="space-y-2 text-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
                      <div><strong>Short name:</strong> {s.school_short_name || '—'}</div>
                      <div><strong>Type:</strong> {humanize(s.school_type)}</div>
                      <div><strong>Location:</strong> {s.city}{s.city && s.state ? ', ' : ''}{s.state}</div>
                      <div><strong>EA deadline:</strong> {s.admission_deadlines.early_action ? format(parseISO(s.admission_deadlines.early_action), 'MMM d, yyyy') : '—'}</div>
                      <div><strong>ED deadline:</strong> {s.admission_deadlines.early_decision ? format(parseISO(s.admission_deadlines.early_decision), 'MMM d, yyyy') : '—'}</div>
                      <div><strong>RD deadline:</strong> {s.admission_deadlines.regular_decision ? format(parseISO(s.admission_deadlines.regular_decision), 'MMM d, yyyy') : '—'}</div>
                      <div><strong>Rolling admissions:</strong> {s.admission_deadlines.rolling ? 'Yes' : 'No'}</div>
                      <div><strong>Decision date:</strong> {s.decision_date ? format(parseISO(s.decision_date), 'MMM d, yyyy') : '—'}</div>
                    </div>
                    {s.notes && <p className="text-soft mt-2">{s.notes}</p>}
                    <div className="flex gap-3 mt-2 text-xs">
                      {s.institutional_aid_link && (
                        <a href={s.institutional_aid_link} target="_blank" rel="noopener noreferrer" className="text-forest-700 hover:underline flex items-center gap-0.5">
                          <ExternalLink size={11} />Financial aid
                        </a>
                      )}
                      {s.honors_program_link && (
                        <a href={s.honors_program_link} target="_blank" rel="noopener noreferrer" className="text-forest-700 hover:underline flex items-center gap-0.5">
                          <ExternalLink size={11} />Honors program
                        </a>
                      )}
                    </div>

                    {/* Enriched data from College Scorecard */}
                    {s.scorecard_data && <ScorecardDetailView snapshot={s.scorecard_data} fetchedAt={s.scorecard_fetched_at} />}

                    {editing && (
                      <div className="flex justify-end gap-2 mt-2">
                        <Button type="button" size="sm" variant="ghost" onClick={() => startEdit(s)}><Pencil size={12} className="mr-1" />Edit</Button>
                        <Button type="button" size="sm" variant="ghost" onClick={() => remove(s)}><Trash2 size={12} className="mr-1" />Delete</Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </Card>
        );
      })}
      {adding && draft && (
        <SchoolEditForm draft={draft} setDraft={setDraft} onSave={save} onCancel={cancel} />
      )}
      {editing && !adding && (
        <Button type="button" size="sm" variant="secondary" onClick={startAdd}>
          <Plus size={12} className="mr-1" />Add target school
        </Button>
      )}
    </div>
  );
}

function SchoolEditForm({ draft, setDraft, onSave, onCancel }: { draft: Partial<TargetSchool>; setDraft: (d: Partial<TargetSchool>) => void; onSave: () => void; onCancel: () => void }) {
  const deadlines = draft.admission_deadlines ?? { early_action: null, early_decision: null, regular_decision: null, rolling: false };

  async function handleSearchSelect(result: SchoolSearchResult) {
    // Show optimistic basics immediately, then fill enriched details from the
    // bundled discovery dataset (v0.2.33 — no live API call).
    const basics: Partial<TargetSchool> = {
      ...draft,
      school_name: result.name,
      city: result.city ?? '',
      state: result.state ?? '',
      scorecard_id: result.scorecard_id,
    };
    setDraft(basics);
    const snapshot = await getDiscoverySnapshotById(result.scorecard_id);
    if (!snapshot) return;
    const inferredShort = result.name.match(/\b[A-Z][A-Z]+\b/)?.[0] ?? '';
    setDraft({
      ...basics,
      school_short_name: basics.school_short_name || inferredShort,
      school_type: snapshot.ownership === 'public'
        ? (snapshot.state === 'NC' ? 'public_in_state' : 'public_out_of_state')
        : 'private',
      scorecard_data: snapshot,
      scorecard_fetched_at: new Date().toISOString(),
      institutional_aid_link: basics.institutional_aid_link || snapshot.website_url,
    });
  }

  return (
    <Card className="p-3 bg-forest-50/40 border-forest-200 space-y-3">
      {/* Search: only shown when adding (no scorecard_id yet means no school picked) */}
      {!draft.scorecard_id && (
        <div className="pb-2 border-b border-forest-200">
          <Label>Search for a school</Label>
          <SchoolSearch onSelect={handleSearchSelect} initialQuery={draft.school_name ?? ''} />
        </div>
      )}
      {draft.scorecard_id && (
        <div className="text-xs text-forest-800 bg-forest-100 rounded px-2 py-1 inline-flex items-center gap-1">
          <Info size={12} />
          Linked to school data (ID {draft.scorecard_id})
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <Label>School name</Label>
          <Input value={draft.school_name ?? ''} onChange={(e) => setDraft({ ...draft, school_name: e.target.value })} placeholder="e.g. High Point University" />
        </div>
        <div>
          <Label>Short name</Label>
          <Input value={draft.school_short_name ?? ''} onChange={(e) => setDraft({ ...draft, school_short_name: e.target.value })} placeholder="e.g. HPU" />
        </div>
        <div>
          <Label>Tier</Label>
          <Select value={draft.tier ?? 'backup'} onChange={(e) => setDraft({ ...draft, tier: e.target.value as TargetSchool['tier'] })}>
            <option value="primary">Primary</option>
            <option value="backup">Backup</option>
          </Select>
        </div>
        <div>
          <Label>Rank</Label>
          <Input type="number" min="1" value={draft.rank ?? 1} onChange={(e) => setDraft({ ...draft, rank: Number(e.target.value) })} />
        </div>
        <div>
          <Label>Type</Label>
          <Select value={draft.school_type ?? 'private'} onChange={(e) => setDraft({ ...draft, school_type: e.target.value as TargetSchool['school_type'] })}>
            <option value="private">Private</option>
            <option value="public_in_state">Public (In-State)</option>
            <option value="public_out_of_state">Public (Out-of-State)</option>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>City</Label>
            <Input value={draft.city ?? ''} onChange={(e) => setDraft({ ...draft, city: e.target.value })} />
          </div>
          <div>
            <Label>State</Label>
            <Select value={draft.state ?? ''} onChange={(e) => setDraft({ ...draft, state: e.target.value })}>
              <option value="">— Select —</option>
              {US_STATES.map((st) => (
                <option key={st.code} value={st.code}>{st.code} — {st.name}</option>
              ))}
            </Select>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div>
          <Label>Early Action deadline</Label>
          <Input type="date" value={deadlines.early_action ?? ''} onChange={(e) => setDraft({ ...draft, admission_deadlines: { ...deadlines, early_action: e.target.value || null } })} />
        </div>
        <div>
          <Label>Early Decision deadline</Label>
          <Input type="date" value={deadlines.early_decision ?? ''} onChange={(e) => setDraft({ ...draft, admission_deadlines: { ...deadlines, early_decision: e.target.value || null } })} />
        </div>
        <div>
          <Label>Regular Decision deadline</Label>
          <Input type="date" value={deadlines.regular_decision ?? ''} onChange={(e) => setDraft({ ...draft, admission_deadlines: { ...deadlines, regular_decision: e.target.value || null } })} />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={deadlines.rolling} onChange={(e) => setDraft({ ...draft, admission_deadlines: { ...deadlines, rolling: e.target.checked } })} />
          Rolling admissions
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={draft.applied ?? false} onChange={(e) => setDraft({ ...draft, applied: e.target.checked })} />
          Already applied
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={draft.admitted ?? false} onChange={(e) => setDraft({ ...draft, admitted: e.target.checked })} />
          Admitted
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <Label>Financial aid link</Label>
          <Input type="url" value={draft.institutional_aid_link ?? ''} onChange={(e) => setDraft({ ...draft, institutional_aid_link: e.target.value || null })} />
        </div>
        <div>
          <Label>Honors program link</Label>
          <Input type="url" value={draft.honors_program_link ?? ''} onChange={(e) => setDraft({ ...draft, honors_program_link: e.target.value || null })} />
        </div>
      </div>
      <div>
        <Label>Notes</Label>
        <Textarea rows={2} value={draft.notes ?? ''} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={onCancel}><X size={12} className="mr-1" />Cancel</Button>
        <Button type="button" size="sm" onClick={onSave}><Check size={12} className="mr-1" />Save</Button>
      </div>
    </Card>
  );
}

// =============================================================================
// ScorecardDetailView — read-only enriched data from College Scorecard
// =============================================================================

function ScorecardDetailView({ snapshot, fetchedAt }: { snapshot: ScorecardSnapshot; fetchedAt: string | null }) {
  return (
    <div className="mt-4 pt-3 border-t border-line space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-soft">College Scorecard Data</h4>
        {fetchedAt && (
          <span className="text-xs text-soft">
            As of {format(parseISO(fetchedAt), 'MMM d, yyyy')}
          </span>
        )}
      </div>

      {/* About */}
      <div>
        <div className="text-xs font-medium text-soft mb-1">About</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
          {snapshot.ownership && <div><strong>Ownership:</strong> {humanize(snapshot.ownership)}</div>}
          {snapshot.region && <div><strong>Region:</strong> {snapshot.region}</div>}
          {snapshot.undergrad_enrollment != null && (
            <div><strong>Undergrad enrollment:</strong> {snapshot.undergrad_enrollment.toLocaleString()}</div>
          )}
          {snapshot.website_url && (
            <div>
              <strong>Website:</strong>{' '}
              <a href={snapshot.website_url} target="_blank" rel="noopener noreferrer" className="text-forest-700 hover:underline inline-flex items-center gap-0.5">
                <ExternalLink size={10} />Visit
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Demographics */}
      {(snapshot.pct_women != null || snapshot.pct_white != null) && (
        <div>
          <div className="text-xs font-medium text-soft mb-1">Student Demographics</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-0.5 text-xs">
            {snapshot.pct_women != null && <div>Women: {pct(snapshot.pct_women)}</div>}
            {snapshot.pct_men != null && <div>Men: {pct(snapshot.pct_men)}</div>}
            {snapshot.pct_white != null && <div>White: {pct(snapshot.pct_white)}</div>}
            {snapshot.pct_black != null && <div>Black: {pct(snapshot.pct_black)}</div>}
            {snapshot.pct_hispanic != null && <div>Hispanic: {pct(snapshot.pct_hispanic)}</div>}
            {snapshot.pct_asian != null && <div>Asian: {pct(snapshot.pct_asian)}</div>}
          </div>
        </div>
      )}

      {/* Admissions */}
      {(snapshot.admission_rate != null || snapshot.sat_25 != null || snapshot.act_25 != null) && (
        <div>
          <div className="text-xs font-medium text-soft mb-1">Admissions</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
            {snapshot.admission_rate != null && (
              <div><strong>Admission rate:</strong> {pct(snapshot.admission_rate)}</div>
            )}
            {(snapshot.sat_25 != null || snapshot.sat_75 != null) && (
              <div><strong>SAT 25th–75th:</strong> {snapshot.sat_25 ?? '—'} – {snapshot.sat_75 ?? '—'}</div>
            )}
            {(snapshot.act_25 != null || snapshot.act_75 != null) && (
              <div><strong>ACT 25th–75th:</strong> {snapshot.act_25 ?? '—'} – {snapshot.act_75 ?? '—'}</div>
            )}
          </div>
        </div>
      )}

      {/* Cost */}
      {(snapshot.in_state_tuition != null || snapshot.out_of_state_tuition != null || snapshot.avg_net_price != null) && (
        <div>
          <div className="text-xs font-medium text-soft mb-1">Cost</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
            {snapshot.in_state_tuition != null && (
              <div><strong>In-state tuition:</strong> {money(snapshot.in_state_tuition)}</div>
            )}
            {snapshot.out_of_state_tuition != null && (
              <div><strong>Out-of-state tuition:</strong> {money(snapshot.out_of_state_tuition)}</div>
            )}
            {snapshot.room_board_on_campus != null && (
              <div><strong>Room & board:</strong> {money(snapshot.room_board_on_campus)}</div>
            )}
            {snapshot.avg_net_price != null && (
              <div><strong>Average net price:</strong> {money(snapshot.avg_net_price)}</div>
            )}
          </div>
        </div>
      )}

      {/* Outcomes */}
      {(snapshot.graduation_rate_6yr != null || snapshot.median_earnings_10yr != null) && (
        <div>
          <div className="text-xs font-medium text-soft mb-1">Outcomes</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
            {snapshot.graduation_rate_6yr != null && (
              <div><strong>6-year graduation rate:</strong> {pct(snapshot.graduation_rate_6yr)}</div>
            )}
            {snapshot.median_earnings_10yr != null && (
              <div><strong>Median earnings (10 yr):</strong> {money(snapshot.median_earnings_10yr)}</div>
            )}
          </div>
        </div>
      )}

      <HelperText>Source: US Department of Education College Scorecard.</HelperText>
    </div>
  );
}

function pct(v: number): string {
  // Scorecard returns rates as decimals (0.45 = 45%) and demographics as decimals too
  return `${Math.round(v * 100)}%`;
}

function money(v: number): string {
  return `$${Math.round(v).toLocaleString()}`;
}
